import React, { type ReactNode } from 'react';
import {
  COUNT_BADGE_SIZE_SM,
  COUNT_BADGE_TONE_NEUTRAL,
  type CountBadgeSize,
  type CountBadgeTone,
} from './CountBadge.constants';
import { Root } from './CountBadge.styled';

export interface CountBadgeProps {
  /** Numeric / text content shown inside the pill. */
  children: ReactNode;
  /** Colour intent — `neutral` (default), `error`, or theme-aware `accent`. */
  tone?: CountBadgeTone;
  /** Visual density — `sm` (default, tab-style) or `xs` (chip-style). */
  size?: CountBadgeSize;
  /**
   * Optional accessible label. When the badge sits next to a label that
   * already describes the count (e.g. "Errors 3"), assistive tech may
   * read the digit twice; pass `ariaLabel` to override or `ariaHidden`
   * to remove the badge from the accessibility tree.
   */
  ariaLabel?: string;
  ariaHidden?: boolean;
  className?: string;
  dataTestId?: string;
}

/**
 * Small tone-coloured count pill. Used inside tabs (`Errors 3`),
 * segmented controls (`All 12`), or any list label that needs a
 * compact total.
 */
const CountBadge: React.FC<CountBadgeProps> = ({
  children,
  tone = COUNT_BADGE_TONE_NEUTRAL,
  size = COUNT_BADGE_SIZE_SM,
  ariaLabel,
  ariaHidden,
  className,
  dataTestId = 'count-badge',
}) => (
  <Root
    $tone={tone}
    $size={size}
    aria-label={ariaLabel}
    aria-hidden={ariaHidden ? 'true' : undefined}
    className={className}
    data-testid={dataTestId}
    data-tone={tone}
    data-size={size}
  >
    {children}
  </Root>
);

export default CountBadge;
